import { ChangeEvent, useContext, useRef, useState, VFC } from 'react';
import ReactModal from 'react-modal';

import updateProfileImage from '@/apis/aniftyBackend/users/updateProfileImage';
import CustomButton from '@/components/CustomButton';
import styles from '@/containers/UserProfilePage/UserProfilePage.module.scss';
import { Web3Context } from '@/contexts/Web3Provider/Web3Provider';
import useAccessToken from '@/hooks/useAccessToken';
import { captureException } from '@/utils/Sentry';

const DEFAULT_PROFILE_IMAGE_PATH =
  '/images/user-profile-page/default-profile-picture.png';

type Status = 'ready' | 'uploading' | 'error';

type Props = {
  currentProfileImageURL?: string | null;
  onUpdated(newPhotoURL: string): void;
};

const EditableProfileImage: VFC<Props> = ({
  currentProfileImageURL,
  onUpdated,
}: Props) => {
  const web3Context = useContext(Web3Context);
  const { accessToken, requestSignature } = useAccessToken();

  const profilePhotoUploadRef = useRef<null | HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<null | File>(null);
  const [imgSrc, setImgSrc] = useState<null | string>(null);
  const [status, setStatus] = useState<Status>('ready');

  const fileReader = new FileReader();

  // display image thumbnail
  fileReader.onloadend = () => {
    if (typeof fileReader.result === 'string') {
      setImgSrc(fileReader.result);
    } else {
      setImgSrc(null);
    }
  };

  // start editing profile image
  const editProfile = async () => {
    if (!profilePhotoUploadRef.current) {
      return;
    }

    // require access token
    if (!accessToken && !(await requestSignature())) {
      return;
    }

    // open file select dialog
    profilePhotoUploadRef.current.click();
  };

  // show confirmation modal on image select
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files.length) {
      return;
    }

    const file = e.target.files[0];

    // set file on state
    setSelectedFile(file);

    // load file
    fileReader.readAsDataURL(file);
  };

  // close edit modal
  const cancelEditing = () => {
    // cannot close while processing
    if (status === 'uploading') {
      return;
    }

    // clear state
    setSelectedFile(null);
    setImgSrc(null);
    setStatus('ready');

    // clear file select
    if (profilePhotoUploadRef.current) {
      profilePhotoUploadRef.current.value = null;
    }
  };

  const handleSubmit = async () => {
    if (status === 'uploading' || !web3Context.currentAccountAddress) {
      return;
    }

    setStatus('uploading');

    try {
      const response = await updateProfileImage(
        web3Context.currentAccountAddress,
        selectedFile
      );
      setStatus('ready');
      // close modal
      setSelectedFile(null);
      onUpdated(response.profilePicUrl);
    } catch (err) {
      captureException(err);
      setStatus('error');
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={editProfile}
        className={styles.editProfileIconButton}
      >
        <img
          src={currentProfileImageURL || DEFAULT_PROFILE_IMAGE_PATH}
          alt="profile picture"
        />
        <div>Edit</div>
      </button>
      <input
        type="file"
        ref={profilePhotoUploadRef}
        accept="image/*"
        onChange={handleFileChange}
        className={styles.hiddenFileInput}
      />
      <ReactModal
        isOpen={!!selectedFile}
        onRequestClose={cancelEditing}
        ariaHideApp={false}
        className={styles.profileImageConfirmationModalContent}
      >
        <div className={styles.profileImageConfirmationModal}>
          {!imgSrc ? (
            <div>Loading...</div>
          ) : (
            <>
              <img src={imgSrc} alt="selected image" />
              <div>
                <CustomButton
                  variant="darkOutlined"
                  onClick={cancelEditing}
                  disabled={status === 'uploading'}
                >
                  Cancel
                </CustomButton>
                <CustomButton
                  onClick={handleSubmit}
                  disabled={status === 'uploading'}
                  loading={status === 'uploading'}
                >
                  Update profile image
                </CustomButton>
              </div>
              {status === 'error' && (
                <div className={styles.error}>Internal Error Occurred</div>
              )}
            </>
          )}
        </div>
      </ReactModal>
    </>
  );
};

export default EditableProfileImage;
